import type { CSSProperties, ReactNode } from 'react';

/**
 * Section wrapper with a 1200px content cap, gutter, and optional dark
 * theme. Sections that want the gradient-hero aesthetic set variant="dark".
 * Everything else (features, pricing, founders) uses the default light body
 * so Cloudscape components render with their intended tokens.
 */
export interface MarketingContainerProps {
  children: ReactNode;
  variant?: 'light' | 'dark';
  paddingY?: 'compact' | 'default' | 'spacious';
  id?: string;
}

const PY: Record<Required<MarketingContainerProps>['paddingY'], number> = {
  compact: 48,
  default: 96,
  spacious: 144,
};

export function MarketingContainer({
  children,
  variant = 'light',
  paddingY = 'default',
  id,
}: MarketingContainerProps): ReactNode {
  const dark = variant === 'dark';
  const wrapper: CSSProperties = {
    position: 'relative',
    overflow: 'hidden',
    background: dark
      ? 'radial-gradient(1000px 500px at 50% 0%, rgba(99, 102, 241, 0.18), transparent 60%), radial-gradient(600px 400px at 20% 40%, rgba(139, 92, 246, 0.12), transparent 60%), #0f1117'
      : '#ffffff',
    color: dark ? '#e2e8f0' : 'inherit',
    paddingTop: PY[paddingY],
    paddingBottom: PY[paddingY],
  };
  const inner: CSSProperties = {
    position: 'relative',
    maxWidth: 1200,
    margin: '0 auto',
    padding: '0 24px',
  };
  return (
    <section id={id} style={wrapper}>
      <div style={inner}>{children}</div>
    </section>
  );
}
